import { INavigationNodeDTO } from "coreframetypings/dto";
import { VisitNodeResponseE } from '@/Enums/VisitNodeResponseE';

export default class NavigationTreeNodeHelper {

    /**
     * Sucht und verschiebt den Knoten mit dem Namen toMove im Baum rootNode in den Knoten moveUnder
     * 
     * @param rootNode Ausgangsknoten der modifiziert werden soll.
     * @param predicate Falls true zurückgegen wird, wird der Knoten ausgewählt und verschoben.
     * @param moveUnder Zielknoten
     * @returns true, wenn der Knoten verschoben wurde.
     */
    public static moveNodeUnder(rootNode: INavigationNodeDTO, predicate: (INavigationNodeDTO) => boolean, moveUnder: INavigationNodeDTO): boolean {
        if (!rootNode?.childNodes)
            return false;

        var nodeToMove = this.findFirstNode(rootNode, predicate);
        if (nodeToMove) {
            this.removeNodes(rootNode, [nodeToMove]);
            return this.appendLast(moveUnder, nodeToMove);
        }
        return false;
    }

    /**
     * Fügt den Knoten nodeToAppend zu dem Knoten roodNode als erstes Kind hinzu. 
     * 
     * @param rootNode Knoten welcher modifiziert werden soll.
     * @param nodeToAppend Knoten der als Kind hinzugefügt werden soll.
     * @returns true, wenn der Knoten hinzugefügt wurde.
     */
    public static appendLast(rootNode: INavigationNodeDTO, nodeToAppend: INavigationNodeDTO): boolean {
        if (!rootNode) {
            return false;
        } else if (!rootNode.childNodes) {
            rootNode.childNodes = [];
        }
        rootNode.childNodes.push(nodeToAppend);
        return true;
    }

    /**
     * Fügt den Knoten nodeToAppend zu dem Knoten roodNode als letztes Kind hinzu. 
     * 
     * @param rootNode Knoten welcher modifiziert werden soll.
     * @param nodeToAppend Knoten der als Kind hinzugefügt werden soll.
     * @returns {boolean} true, wenn der Knoten hinzugefügt wurde.
     */
    public static appendFirst(rootNode: INavigationNodeDTO, nodeToAppend: INavigationNodeDTO): boolean {
        if (!rootNode) {
            return false;
        } else if (!rootNode.childNodes) {
            rootNode.childNodes = [];
        }
        rootNode.childNodes.unshift(nodeToAppend);
        return true;
    }

    /**
     * Sucht nach dem ersten passenden Knoten im Baum.
     * 
     * @param rootNode Ausgangsknoten
     * @param predicate Falls true zurückgegen wird, wird der Knoten ausgewählt.
     * @returns NavigationNode Objekt.
     */
    public static findFirstNode(rootNode: INavigationNodeDTO, predicate: (node: INavigationNodeDTO) => boolean): INavigationNodeDTO {
        let returnNode = null;
        this.visit(rootNode, node => {
            if (predicate(node)) {
                returnNode = node;
                return VisitNodeResponseE.Return;
            }
            return VisitNodeResponseE.Continue;
        }, true);
        return returnNode;
    }

    /**
     * Entfernt einen Knoten aus dem Baum und gibt diesen zurück.
     * 
     * @param rootNode Ausgangsknoten
     * @param predicate Falls true zurückgegen wird, wird der Knoten ausgewählt und entfernt.
     * @returns Aus dem Baum entferntes Knoten Objekt.
     */
    public static findFirstAndRemoveNode(rootNode: INavigationNodeDTO, predicate: (node: INavigationNodeDTO) => boolean): INavigationNodeDTO {
        let returnNode = null;
        this.visit(rootNode, node => {
            if (this.hasChildren(node)) {
                let nodes = node.childNodes.filter(predicate);
                if (nodes.length > 0) {
                    returnNode = nodes[0];
                    node.childNodes = node.childNodes.filter(n => n !== returnNode);
                    return VisitNodeResponseE.Return;
                }
            }
            return VisitNodeResponseE.Continue;
        }, true);
        return returnNode;
    }

    /**
     * Findet alle Knoten im Baum die das Predicate erfüllen.
     * 
     * @param rootNode Ausgangsknoten
     * @param predicate Falls true zurückgegen wird, wird der Knoten zur Ausgabe hinzugefügt.
     * @returns Array mit den gefundenen Knoten im Baum
     */
    public static findNodes(rootNode: INavigationNodeDTO, predicate: (node: INavigationNodeDTO) => boolean): INavigationNodeDTO[] {
        let nodes = [];
        this.visit(rootNode, node => {
            if (predicate(node)) {
                nodes.push(node);
            }
            return VisitNodeResponseE.Continue;
        }, true);
        return nodes;
    }

    /**
     * Findet alle Kindknoten die das Predicate erfüllen. 
     * 
     * @param rootNode Ausgangsknoten
     * @param predicate Falls true zurückgegen wird, wird der Knoten zur Ausgabe hinzugefügt.
     * @return Array mit den gefundenen Knoten
     */
    public static findInChildNodes(rootNode: INavigationNodeDTO, predicate: (node: INavigationNodeDTO) => boolean): INavigationNodeDTO[] {
        let nodes = [];
        this.visit(rootNode, node => {
            if (predicate(node)) {
                nodes.push(node);
            }
            return VisitNodeResponseE.Skip;
        }, false);
        return nodes;
    }

    /**
     * Gibt den Enternknoten für den ersten passenden Knoten vom predicate zurück.
     * 
     * @param rootNode Ausgangsknoten
     * @param predicate Falls true zurückgegen wird, wird der Knoten ausgewählt und der Elternknoten zurückgegeben.
     * @returns Elternknoten des Knoten
     */
    public static findParentNode(rootNode: INavigationNodeDTO, predicate: (node: INavigationNodeDTO) => boolean): INavigationNodeDTO {
        let returnNode = null;
        this.visit(rootNode, node => {
            if (this.hasChildren(node)) {
                if (node.childNodes.some(predicate)) {
                    returnNode = node;
                    return VisitNodeResponseE.Return;
                }
            }
            return VisitNodeResponseE.Continue;
        }, true);
        return returnNode;
    }

    /**
     * Entfernt alle Knoten aus dem Baum die predicate erfüllen.
     * 
     * @param rootNode Ausgangsknoten
     * @param ergnameToFind Falls true zurückgegen wird, wird der Knoten entfernt.
     * @returns true, wenn der Knoten erntfernt wurde.
     */
    public static removeAllNodes(rootNode: INavigationNodeDTO, predicate: (node: INavigationNodeDTO) => boolean): void {
        this.visit(rootNode, node => {
            if (this.hasChildren(node)) {
                node.childNodes = node.childNodes.filter(n => !predicate(n));
            }
            return VisitNodeResponseE.Continue;
        }, true);
    }

    /**
     * Entfernt alle Knoten Objekte aus dem Baum
     * 
     * @param rootNode Ausgangsknoten
     * @param nodesToRemove Knoten die entfernt werden sollen.
     */
    public static removeNodes(rootNode: INavigationNodeDTO, nodesToRemove: INavigationNodeDTO[]): void {
        this.visit(rootNode, node => {
            if (this.hasChildren(node)) {
                node.childNodes = node.childNodes.filter(n => nodesToRemove.indexOf(n) == -1);
            }
            return VisitNodeResponseE.Continue;
        }, true);
    }

    /**
     * Ruft für alle Kindknoten die Funktiuon fn(node) auf.
     * Die Iteration bricht ab, wenn die Funktion fn false zurückgibt.
     * Wenn die Funktion "skip" zurückgibt wird der aktuelle Zweig übersprungen.
     *  
     * @param visitNode Der Startknoten
     * @param fn Callback Funktion für jedes Element im Baum. 
     * @param includeSelf Wenn true, wird das Startknoten an die Funktion übergeben.
     * @returns false, wenn die Iteration abgebrochen wurde.
     */
    public static visit(visitNode: INavigationNodeDTO, fn: (node: INavigationNodeDTO) => VisitNodeResponseE, includeSelf: boolean = false): boolean {
        if (visitNode) {
            if (includeSelf === true) {
                let res = fn(visitNode);
                if (res === VisitNodeResponseE.Return) {
                    //Bereche Iteration bei dieser Node ab und gebe false zurück.
                    return false;
                } else if (res === VisitNodeResponseE.Skip) {
                    //Überspringe diese Node und behandle keine weiteren Kinder dieser Node. 
                    return true;
                }
            }

            let visitCompleted = true;
            if (visitNode.childNodes) {
                let children = visitNode.childNodes;
                for (let i = 0; i < children.length; i++) {
                    if ((visitCompleted = this.visit(children[i], fn, true)) === false) {
                        //Iteration wurde abgebrochen
                        break;
                    }
                }
            }

            return visitCompleted;
        }
        return false;
    }

    /**
     * Prüft, ob ein Knoten Kindknoten besitzt.
     * 
     * @param node Knoten der geprüft werden soll.
     * @returns true, wenn der Knoten null oder keine Kinder besitzt.
     */
    public static hasChildren(node: INavigationNodeDTO): boolean {
        if (node?.childNodes && node.childNodes.length > 0) {
            return true;
        }
        return false;
    }

    /**
     * Suche und ersetzte den Knoten mit dem Namen ergnameToFind im Baum rootNode gegen den Knoten replaceNode.
     * 
     * @param rootNode Ausgangsknoten
     * @param node Knoten der ersetzt werden soll.
     * @param replaceNode Neues Knoten Objekt welches ersetzt wird.
     * @returns true, wenn erfolgreich.
     */
    public static replaceNode(rootNode: INavigationNodeDTO, node: INavigationNodeDTO, replaceNode: INavigationNodeDTO): boolean {
        return this.visit(rootNode, n => {
            if (this.hasChildren(n)) {
                let index = n.childNodes.indexOf(node);
                if (index > -1) {
                    n.childNodes[index] = replaceNode;
                    return VisitNodeResponseE.Return;
                }
            }

            return VisitNodeResponseE.Continue;
        }, true) === false;
    }

    /**
     * Erzeugt ein Predicate für ein Regex, dass die Knoten nach ergname oder name auswählt.
     * 
     * @param regex Regex das die Knoten auswählt
     * @returns Predicate für das Regex
     */
    public static matchNodeByRegex(regex: RegExp): (INavigationNodeDTO) => boolean {
        return node => (node.ergName != null && regex.test(node.ergName))
            || (node.name != null && regex.test(node.name));
    }

    /**
     * Erzeugt ein Predicate, welches die Knoten auswählt die ergnameToFind in ergname oder name enthalten.
     * 
     * @param ergnameToFind Suchterm für die Knoten
     * @returns Predicate für den Suchterm
     */
    public static matchNodeWithName(ergnameToFind: string): (INavigationNodeDTO) => boolean {
        return node => (node.ergName != null && node.ergName.toLowerCase().indexOf(ergnameToFind.toLowerCase()) >= 0)
            || (node.name != null && node.name.toLowerCase().indexOf(ergnameToFind.toLowerCase()) >= 0)
    }
}